import { useCallback } from 'react'
import { cn } from '@/true-fsd/shared/utils/css'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { BackgroundOffIcon } from './icons/BackgroundOffIcon'
import { BackgroundOnIcon } from './icons/BackgroundOnIcon'
import classes from './BackgroundToggleButton.module.scss'

/**
 * Пропсы для компонента BackgroundToggleButton
 */
export interface BackgroundToggleButtonProps {
    /**
     * Текущее состояние подложки (включена/выключена)
     */
    value: boolean
    /**
     * Обработчик изменения состояния подложки
     */
    onChange?: (value: boolean) => void
    /**
     * Дополнительные CSS классы
     */
    className?: string
    /**
     * Отключена ли кнопка
     */
    disabled?: boolean
}

/**
 * Компонент кнопки-переключателя подложки текста
 * При наведении отображает тултип "Фон текста"
 * При клике переключает состояние подложки (вкл/выкл)
 */
export function BackgroundToggleButton({
    value,
    onChange,
    className,
    disabled = false,
}: BackgroundToggleButtonProps) {
    /**
     * Обработчик клика по кнопке
     * Переключает состояние подложки
     */
    const handleClick = useCallback(() => {
        if (disabled || !onChange) {
            return
        }
        onChange(!value)
    }, [disabled, onChange, value])

    /**
     * Получить иконку в зависимости от состояния
     */
    const getIcon = useCallback(() => {
        if (value) {
            return <BackgroundOnIcon className={classes.icon} />
        }
        return <BackgroundOffIcon className={classes.iconOff} />
    }, [value])

    return (
        <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    className={cn(classes.button, className, {
                        [classes.buttonDisabled]: disabled,
                        [classes.buttonActive]: value,
                    })}
                    onClick={handleClick}
                    disabled={disabled}
                    aria-label="Включить/выключить подложку"
                    aria-pressed={value}
                >
                    {getIcon()}
                </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={4} className="z-[60]">
                Фон текста
            </TooltipContent>
        </Tooltip>
    )
}

